import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from './FormManager';
import './ServicesPage.css';
import { Helmet } from 'react-helmet';

const serviceIcons = {
  website: '💻️',
  google: '🔍',
  ads: '📣',
  booking: '📅',
  reputation: '⭐',
  email: '📧'
};

const ServicesPage = () => {
  const { t } = useTranslation();
  const { openGeneralForm } = useForm();

  const heroRef = useRef(null);
  const servicesGridRef = useRef(null);
  const processRef = useRef(null);
  const ctaRef = useRef(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const handleFaqToggle = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  useEffect(() => {
    // Make all content visible by default, bypassing animation logic
    const elementsToMakeVisible = [
      '.services-page-hero',
      '.services-page-title',
      '.services-page-description',
      '.services-page-grid',
      '.service-detail-item',
      '.services-page-process',
      '.process-card',
      '.process-note',
      '.services-page-cta',
      '.services-page-faq'
    ];
    elementsToMakeVisible.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => el.classList.add('visible'));
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('servicesPageTitle')}</title>
        <meta name="description" content={t('servicesPageDescription')} />
        <link rel="canonical" href="https://enzlo.com/services" />

        {/* SEO Structured Data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Enzlo",
            "url": "https://enzlo.com",
            "logo": "https://enzlo.com/logo192.png",
            "address": [
              {"@type": "PostalAddress", "streetAddress": "Eckweg 8", "postalCode": "64354", "addressLocality": "Reinheim", "addressCountry": "DE"},
              {"@type": "PostalAddress", "streetAddress": "Zeppelinstraße 20", "postalCode": "73084", "addressLocality": "Salach", "addressCountry": "DE"}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "${t('servicesPageTitle')}",
            "description": "${t('servicesPageDescription')}",
            "url": "https://enzlo.com/services"
          }
        `}</script>
        <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {"@type": "Question", "name": "${t('faqQuestion1')}", "acceptedAnswer": {"@type": "Answer", "text": "${t('faqAnswer1')}"}},
            {"@type": "Question", "name": "${t('faqQuestion2')}", "acceptedAnswer": {"@type": "Answer", "text": "${t('faqAnswer2')}"}},
            {"@type": "Question", "name": "${t('faqQuestion3')}", "acceptedAnswer": {"@type": "Answer", "text": "${t('faqAnswer3')}"}}
          ]
        }
        `}</script>
        <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://enzlo.com/"},
            {"@type": "ListItem", "position": 2, "name": "Leistungen", "item": "https://enzlo.com/services"}
          ]
        }
        `}</script>

        {/* OpenGraph & Twitter Card */}
        <meta property="og:title" content={t('servicesPageTitle')} />
        <meta property="og:description" content={t('servicesPageDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://enzlo.com/services" />
        <meta property="og:image" content="https://enzlo.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('servicesPageTitle')} />
        <meta name="twitter:description" content={t('servicesPageDescription')} />
        <meta name="twitter:image" content="https://enzlo.com/og-image.jpg" />
      </Helmet>

      <main>
        {/* Hero Section */}
        <section className="services-page-hero" ref={heroRef}>
          <div className="services-page-background">
            <div className="services-page-shape services-page-shape-1"></div>
            <div className="services-page-shape services-page-shape-2"></div>
          </div>
          <div className="container">
            <div className="services-page-content">
              <h1 className="services-page-title">{t('servicesTitle')}</h1>
              <p className="services-page-description">{t('servicesDescription')}</p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="services-page-details">
          <div className="container">
            <div className="services-page-grid" ref={servicesGridRef}>
              <div className="service-detail-item" id="webdesign-frankfurt">
                <div className="service-detail-icon">{serviceIcons.website}</div>
                <div className="service-detail-content">
                  <h2 className="service-detail-title">{t('serviceItem1Title')}</h2>
                  <div className="service-detail-text">{t('serviceItem1')}</div>
                  <div className="service-detail-features">
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('webdesignFeature1')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('webdesignFeature2')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('webdesignFeature3')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('webdesignFeature4')}</span></div>
                  </div>
                </div>
              </div>
              <div className="service-detail-item" id="seo-frankfurt">
                <div className="service-detail-icon">{serviceIcons.google}</div>
                <div className="service-detail-content">
                  <h2 className="service-detail-title">{t('serviceItem2Title')}</h2>
                  <div className="service-detail-text">{t('serviceItem2')}</div>
                  <div className="service-detail-features">
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('seoFeature1')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('seoFeature2')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('seoFeature3')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('seoFeature4')}</span></div>
                  </div>
                </div>
              </div>
              <div className="service-detail-item" id="google-ads-frankfurt">
                <div className="service-detail-icon">{serviceIcons.ads}</div>
                <div className="service-detail-content">
                  <h2 className="service-detail-title">{t('serviceItem3Title')}</h2>
                  <div className="service-detail-text">{t('serviceItem3')}</div>
                  <div className="service-detail-features">
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('adsFeature1')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('adsFeature2')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('adsFeature3')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('adsFeature4')}</span></div>
                  </div>
                </div>
              </div>
              <div className="service-detail-item" id="buchungssystem-frankfurt">
                <div className="service-detail-icon">{serviceIcons.booking}</div>
                <div className="service-detail-content">
                  <h2 className="service-detail-title">{t('serviceItem4Title')}</h2>
                  <div className="service-detail-text">{t('serviceItem4')}</div>
                  <div className="service-detail-features">
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('bookingFeature1')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('bookingFeature2')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('bookingFeature3')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('bookingFeature4')}</span></div>
                  </div>
                </div>
              </div>
              <div className="service-detail-item" id="reputation-management-frankfurt">
                <div className="service-detail-icon">{serviceIcons.reputation}</div>
                <div className="service-detail-content">
                  <h2 className="service-detail-title">{t('serviceItem5Title')}</h2>
                  <div className="service-detail-text">{t('serviceItem5')}</div>
                  <div className="service-detail-features">
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('reputationFeature1')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('reputationFeature2')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('reputationFeature3')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('reputationFeature4')}</span></div>
                  </div>
                </div>
              </div>
              <div className="service-detail-item" id="email-marketing-frankfurt">
                <div className="service-detail-icon">{serviceIcons.email}</div>
                <div className="service-detail-content">
                  <h2 className="service-detail-title">{t('serviceItem6Title')}</h2>
                  <div className="service-detail-text">{t('serviceItem6')}</div>
                  <div className="service-detail-features">
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('emailMarketingFeature1')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('emailMarketingFeature2')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('emailMarketingFeature3')}</span></div>
                    <div className="service-detail-feature"><span className="feature-check">✓</span><span>{t('emailMarketingFeature4')}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="services-page-process" ref={processRef}>
          <div className="process-background">
            <div className="process-shape process-shape-1"></div>
            <div className="process-shape process-shape-2"></div>
          </div>
          <div className="container">
            <div className="process-header">
              <h2 className="process-title">{t('simpleProcessTitle')}</h2>
              <p className="process-description">{t('simpleProcessDescription')}</p>
            </div>
            <div className="process-timeline">
              <div className="timeline-track"></div>
              {/* Process Card 1 */}
              <div className="process-card">
                <div className="process-card-inner">
                  <div className="process-card-front">
                    <div className="process-card-number">1</div>
                    <div className="process-card-title">{t('processStep1Title')}</div>
                  </div>
                  <div className="process-card-back">
                    <p className="process-card-text">{t('simpleProcessStep1')}</p>
                  </div>
                </div>
              </div>
              {/* Process Card 2 */}
              <div className="process-card">
                <div className="process-card-inner">
                  <div className="process-card-front">
                    <div className="process-card-number">2</div>
                    <div className="process-card-title">{t('processStep2Title')}</div>
                  </div>
                  <div className="process-card-back">
                    <p className="process-card-text">{t('simpleProcessStep2')}</p>
                  </div>
                </div>
              </div>
              {/* Process Card 3 */}
              <div className="process-card">
                <div className="process-card-inner">
                  <div className="process-card-front">
                    <div className="process-card-number">3</div>
                    <div className="process-card-title">{t('processStep3Title')}</div>
                  </div>
                  <div className="process-card-back">
                    <p className="process-card-text">{t('simpleProcessStep3')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="process-note">
              <span className="note-icon">💡</span>
              <p className="note-text">{t('processNote')}</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="services-page-cta" ref={ctaRef}>
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">{t('finalCtaTitle')}</h2>
              <p className="cta-description">{t('finalCtaDescription')}</p>
              <button className="cta-button" onClick={openGeneralForm}>
                <span className="cta-text">{t('finalCtaPrimary')}</span>
                <span className="cta-arrow">→</span>
              </button>
              <p className="cta-note">{t('ctaNote')}</p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="services-page-faq">
          <div className="container">
            <h2 className="faq-title">{t('faqTitle')}</h2>
            <div className="faq-list">
              {[1, 2, 3, 4, 5].map((item, index) => (
                <div 
                  key={index} 
                  className={`faq-item ${openFaqIndex === index ? 'active' : ''}`}
                  onClick={() => handleFaqToggle(index)}
                >
                  <div className="faq-question">
                    <h3>{t(`faq${item}Question`)}</h3>
                    <span className="faq-toggle">{openFaqIndex === index ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>{t(`faq${item}Answer`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default ServicesPage;